import React, { useMemo, useState } from 'react';
import { Form } from 'semantic-ui-react';
import { Folio } from '../../api/services/next';
import { DatePicker } from '../../components/shared/DatePicker';
import HotelsDropDown from '../../components/shared/HotelsDropDown';
import TextInput from '../../components/shared/TextInput';
import { useTextResources } from '../../language';
import GridSearchTemplate from '../GridSearchTemplate';
import moment from 'moment';
import { endpoint } from '../../api/http';
import CustomerDropDown from '../../components/shared/CustomerDropDown';
import ReservationStatusesDropDown from '../../components/shared/ReservationStatusesDropDown';
import GuestSearchDropDown from '../../components/shared/GuestSearchDropDown';
import PropTypes from 'prop-types';
import { EntityType, dateFormat } from '../../components/shared/constants';
import TagsDropDown from '../../components/shared/TagsDropDown';
import FolioTypesDropDown from '../../components/shared/FolioTypeDropDown';
import { currencyFormat } from '../../components/shared/utils';
import formatCurrency from 'format-currency';
import { ReservationStatusesEnums } from '../../components/shared/statustypes';

const FolioSearch = () => {
  const text = useTextResources();
  const [hotelIds, setHotelIds] = useState([]);
  const [guest, setGuest] = useState({});
  
  const dateFormatter = ({ value }) => {
    return value && moment(value).format(dateFormat) || null;
  };

  
  const folioFormatter = ({ value, row }) => {
    if(row) {
      return <a
        target='_blank'
        rel='noopener noreferrer'
        href={`${endpoint}${row.ReservationId?`Finance/Folio/Folio?ReservationId=${row.ReservationId}&HotelId=${row.HotelId}`:`Finance/Folio/Folio?FolioId=${row.Id}?HotelId=${row.HotelId}`}`}
      >
        {value}
      </a>
    }
    else return value || "";
  };
  const reservationFormatter = ({ value, row }) => {
    if(row) {
      return (
        <a
          target='_blank'
          rel='noopener noreferrer'
          href={`${endpoint}PMS/Reservations/Index/${row.ReservationId}?hotelid=${row.HotelId}`}
        >
          {value}
        </a>)
    }
    else return value || "";
  };

  const guestFormatter = ({ value, row }) => {
      return row ? (
          <a
              target='_blank'
              rel='noopener noreferrer'
              href={`${endpoint}CRM/GuestProfile/index/?hotelid=${row.HotelId}#/profile/${row.GuestId}`}
          >
              {value}
          </a>
      ) : value || ""; 
  };
  const currencyFormatter = ({row, value }) => {
    return formatCurrency(value || 0, currencyFormat(row.CurrencyId));
  }
  
  const CustomersSpec = React.useMemo(() => {
    return {
      GetOnlyCustomerArray: true,
      IsActive: true,
      IncludeCustomerTypeId: true,
    };
  }, []);

  const onChangeGuestvalue = (value) => {
    if (!value?.toString().startsWith('newguest-')) {
      setGuest({ GuestId: value });
    }
  };

  const datePredicate = (value, filter) => {
    return dateFormatter({value})?.includes(filter?.value?.toLowerCase())
  }

  const gridOptions = {
    search: {
      fields: [
        <Form.Field
          key='HotelIds'
          onChange={setHotelIds}
          value={hotelIds}
          multiple
          label={text('Hotel')}
          control={HotelsDropDown}
          spec={useMemo(
            () => ({
              IsActive: true,
              IncludeOnlyHotelsWithUserPermissions: true,
            }),
            []
          )}
          placeholder={text('All')}
          clearable
        />,
        <Form.Field
          key='Id'
          label={text('FolioNumber')}
          control={TextInput}
        />,
        <Form.Field
          key='ReservationNumber'
          label={text('ReservationNumber')}
          control={TextInput}
        />,
        <DatePicker
          picktype='range'
          label={text('ArrivalDate')}
          key='CheckInDateRange'
        />,
        <DatePicker
          picktype='range'
          label={text('DepartureDate')}
          key='CheckOutDateRange'
        />,
        <Form.Field
          key='ReservationStatuses'
          label={text('ReservationStatus')}
          placeholder={text('All')}
          multiple
          clearable
          control={ReservationStatusesDropDown}
        />,
        <Form.Field
          key='GuestId'
          label={text('Guest')}
          width={3}
          clearable
          control={GuestSearchDropDown}
          placeholder='Search by name, email, phone or id'
          additionLabel={`${text('Search')}: `}
          onAddGuest={setGuest}
          onChange={onChangeGuestvalue}
        />,
      ],
      advancedFields: [
        <Form.Field
          key='InvoiceNumber'
          label={text('InvoiceNumber')}
          control={TextInput}
        />,
        <DatePicker
          picktype='range'
          label={text('InvoiceDate')}
          key='InvoiceDateRange'
        />,
        <Form.Field
          key='CustomerID'
          label={text('Customer')}
          placeholder={text('All')}
          width={3}
          clearable
          control={CustomerDropDown}
          spec={CustomersSpec}
        />,
        <Form.Field
          key='Voucher'
          label={text('Voucher')}
          control={TextInput}
        />,
        <Form.Field
          key='FolioOwnerType'
          control={FolioTypesDropDown}
          placeholder={text('All')}
          clearable
          label={text('FolioOwnerType')}
        />,
        <Form.Field
          key='Tags'
          label={text('Tags')}
          placeholder={text('Select')}
          width={3}
          clearable
          control={TagsDropDown}
          IsSystem
          multiple
          allowAdditions={false}
          EntityTypeId={EntityType.Folio}
        />
      ],
      method: 'POST',
      api: Folio.Search,
      searchOnLoad: true,
      initial: {
        FolioOwnerType: 1,
        ReservationStatuses: [ReservationStatusesEnums.CheckedIn]
      },
      spec: {
        MaxResults: 1000,
        FreeText:
          (guest?.FirstName || guest?.LastName) &&
          `${guest?.FirstName} ${guest?.LastName}`,
        GuestId: guest?.GuestId?.toString().startsWith('newguest-')
          ? null
          : guest?.GuestId,
      }
    },
    grid: {
        columns: [
        { name: 'Hotel', title: text('Hotel') },
        {
          name: 'Id',
          title: text('Folio'),
          formatter: folioFormatter,
        },
        { name: 'Owner', title: text('Owner') },
        { name: 'GuestName', title: text('Guest'), formatter: guestFormatter },
        { name: 'Company', title: text('Company') },
        { name: 'Agent', title: text('Agent') },
        { name: 'InvoiceNumber', title: text('InvoiceNumber')},
        { name: 'InvoiceDate', title: text('InvoiceDate'), formatter: dateFormatter },
        { name: 'ReservationNumber', title: text('Reservation'), formatter: reservationFormatter },
        {
            name: 'CheckInDate',
            title: text('ArrivalDate'),
            formatter: dateFormatter,
        },
        {
            name: 'CheckOutDate',
            title: text('DepartureDate'),
            formatter: dateFormatter,
        },
        { name: 'Voucher', title: text('Voucher')},
        { name: 'Balance', title: text('Balance'), formatter: currencyFormatter },
        { name: 'Status', title: text('Status') }
      ],
      paging: true,
      scrollbarX:true,
      cacheId:'FolioSearch',
        customOptions:{
          hidden: {
            toggleHidden:true
           },
            group: {},
            order:{},
            sort:{
              sorting:[{ columnName: 'Number', direction: 'desc' }]
            }
        },
      filtering: {
        showByDefault:true,
        extensions: [
          {columnName: 'CheckInDate', predicate: datePredicate},
          {columnName: 'CheckOutDate', predicate: datePredicate}
        ]
      },
    },
  };
  return <GridSearchTemplate options={gridOptions} />;
};

FolioSearch.propTypes = {
  hotelId: PropTypes.any,
};
export default FolioSearch;
